import React, { useEffect, useRef, useState } from "react";
import cover1 from "../../Assets/cover1.webp";
import cover2 from "../../Assets/cover2.webp";
import cover3 from "../../Assets/cover3.webp";
import cover4 from "../../Assets/cover4.webp";
import "./WelcomeVideos.css";

// Paste each video's link into `url` when it's ready. YouTube links
// (watch, youtu.be, shorts) are embedded; any other link should be a direct
// video file (.mp4/.webm). Leave `url` empty to show the "coming soon" card.
//
// `cover` is an optional picture shown until the visitor presses play (16:9
// works best); import it from src/Assets like cover1-4 below. Without one, a
// YouTube link shows the video's own YouTube thumbnail.
const VIDEOS = [
  {
    title: "Discover Website Features",
    text: "A quick tour of the menus, search and guides that help you practise.",
    url: "https://www.youtube.com/watch?v=pjcY8oNLNUo",
    cover: cover1,
  },
  {
    title: "Explore Yoga Poses",
    text: "Find poses, variations and their benefits across the YogaRefer library.",
    url: "https://www.youtube.com/watch?v=bEd2XAPBZV4",
    cover: cover2,
  },
  {
    title: "How to Contribute",
    text: "Upload your own yoga pose variations and share your knowledge with the world.",
    url: "https://www.youtube.com/watch?v=j5I32hZI2Dc",
    cover: cover3,
  },
  {
    title: "Explore Yoga in Your Preferred Language",
    text: "Discover yoga terms, Sanskrit meanings, and ancient wisdom in 17 languages, making your yoga journey easier to understand and explore.",
    url: "https://www.youtube.com/watch?v=vRoZiAi5ujM",
    cover: cover4,
  },
];

const getYouTubeId = (url) => {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/,
  );
  return match ? match[1] : null;
};

const PlayBadge = () => (
  <span className="welcome-video-play" aria-hidden="true">
    <svg viewBox="0 0 24 24" width="24" height="24">
      <path d="M8 5v14l11-7z" fill="currentColor" />
    </svg>
  </span>
);

// YouTube's player only loads once the cover is clicked. That lets the card
// show our own picture, and keeps the page from loading a player per card.
const YouTubePlayer = ({ id, title, cover }) => {
  const [playing, setPlaying] = useState(false);
  const [noMaxRes, setNoMaxRes] = useState(false);
  const playerRef = useRef(null);

  // The cover button disappears when the player replaces it, so pass focus on
  useEffect(() => {
    if (playing) playerRef.current?.focus();
  }, [playing]);

  if (playing) {
    return (
      <iframe
        ref={playerRef}
        className="welcome-video-media"
        src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  const thumbnail = `https://i.ytimg.com/vi/${id}/${noMaxRes ? "hqdefault" : "maxresdefault"}.jpg`;

  return (
    <button
      type="button"
      className="welcome-video-cover"
      onClick={() => setPlaying(true)}
      aria-label={`Play video: ${title}`}
    >
      <img
        src={cover || thumbnail}
        alt=""
        loading="lazy"
        onLoad={(e) => {
          // Videos without an HD thumbnail get a 120px-wide stand-in instead
          if (!cover && e.currentTarget.naturalWidth <= 120) setNoMaxRes(true);
        }}
      />
      <PlayBadge />
    </button>
  );
};

const VideoFrame = ({ video }) => {
  if (!video.url) {
    return (
      <div
        className={`welcome-video-placeholder${video.cover ? " has-cover" : ""}`}
        aria-label="Video coming soon"
      >
        {video.cover && <img src={video.cover} alt="" loading="lazy" />}
        <PlayBadge />
        <span className="welcome-video-soon">Video coming soon</span>
      </div>
    );
  }

  const youTubeId = getYouTubeId(video.url);
  if (youTubeId) {
    return <YouTubePlayer id={youTubeId} title={video.title} cover={video.cover} />;
  }

  return (
    <video
      className="welcome-video-media"
      src={video.url}
      poster={video.cover || undefined}
      controls
      preload="metadata"
    />
  );
};

const WelcomeVideos = () => (
  <section className="welcome-videos" aria-labelledby="welcome-videos-title">
    <div className="welcome-videos-header">
      <h2 id="welcome-videos-title">
        Welcome to YogaRefer: Your Guide to Exploring and Contributing Yoga
        Knowledge
      </h2>
      <p>
        Explore yoga poses, discover website features, and learn how to
        contribute your knowledge to a growing global yoga community.
      </p>
    </div>

    <div className="welcome-videos-grid">
      {VIDEOS.map((video) => (
        <article className="welcome-video-card" key={video.title}>
          <div className="welcome-video-frame">
            <VideoFrame video={video} />
          </div>
          <div className="welcome-video-body">
            <h3>{video.title}</h3>
            <p>{video.text}</p>
          </div>
        </article>
      ))}
    </div>
  </section>
);

export default WelcomeVideos;
